import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import EmergencyHero from './components/EmergencyHero';
import QuickDialBar from './components/QuickDialBar';
import ContactsGrid from './components/ContactsGrid';
import EmergencyServicesMap from './components/EmergencyServicesMap';
import SafetyTipsSection from './components/SafetyTipsSection';
import AlertSourcesSection from './components/AlertSourcesSection';
import PrintableEmergencyCard from './components/PrintableEmergencyCard';
import EmergencyReportForm from './components/EmergencyReportForm';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function EmergencyPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useSEO({
    title: 'Roseville Emergency Resources | Safety Contacts & Preparedness Guide',
    description: 'Essential emergency contacts and safety resources for Roseville, California. Police, fire, medical, crisis hotlines, utility emergencies, wildfire preparedness, earthquake readiness, and community safety information.',
    keywords: 'Roseville emergency, Roseville police, Roseville fire department, emergency contacts Roseville CA, safety resources Placer County',
    canonical: '/emergency',
    ogImage: 'https://public.readdy.ai/ai/img_res/8faf99f963d779ac6de065eab888785f.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Emergency Resources & Safety Information - Roseville, California',
          'Essential emergency contacts, safety tips, and preparedness resources for Roseville residents and visitors',
          '/emergency'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Emergency Resources', url: '/emergency' },
        ]),
        {
          '@type': 'EmergencyService',
          name: 'Roseville Emergency Services',
          description: 'Comprehensive emergency contact directory for Roseville, California',
          areaServed: {
            '@type': 'City',
            name: 'Roseville',
            '@id': 'https://en.wikipedia.org/wiki/Roseville,_California',
          },
          availableService: [
            {
              '@type': 'Service',
              name: 'Emergency Police/Fire/Medical',
              telephone: '911',
              serviceType: 'Emergency Response',
            },
            {
              '@type': 'Service',
              name: 'Roseville Police Department',
              telephone: '(916) 774-5000',
              serviceType: 'Police Non-Emergency',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '1051 Junction Blvd',
                addressLocality: 'Roseville',
                addressRegion: 'CA',
                postalCode: '95678',
                addressCountry: 'US',
              },
            },
            {
              '@type': 'Service',
              name: 'Roseville Fire Department',
              telephone: '(916) 774-5800',
              serviceType: 'Fire Non-Emergency',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '316 Vernon Street',
                addressLocality: 'Roseville',
                addressRegion: 'CA',
                postalCode: '95678',
                addressCountry: 'US',
              },
            },
            {
              '@type': 'Service',
              name: 'Poison Control Center',
              telephone: '(800) 222-1222',
              serviceType: '24/7 Poison Emergency',
            },
            {
              '@type': 'Service',
              name: 'Suicide & Crisis Lifeline',
              telephone: '988',
              serviceType: 'Mental Health Crisis',
            },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is the non-emergency police number for Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The Roseville Police Department non-emergency number is (916) 774-5000, available 24/7 for non-urgent matters.',
              },
            },
            {
              '@type': 'Question',
              name: 'Where is the nearest emergency room in Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Sutter Roseville Medical Center (1 Medical Plaza Dr) and Kaiser Permanente Roseville (1600 Eureka Rd) both have 24/7 emergency rooms.',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I report a power outage in Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Call Roseville Electric Utility at (916) 774-5748 to report power outages. The line is available 24/7.',
              },
            },
          ],
        },
        {
          '@type': 'SpeakableSpecification',
          cssSelector: ['.emergency-numbers', '.quick-dial-bar'],
          xpath: [
            '/html/head/meta[@name=\'description\']/@content',
            "//div[contains(@class, 'emergency-numbers')]",
          ],
        },
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="font-black">ROSEVILLE</span>
                <span className="text-teal-500">.org</span>
              </div>
            </Link>
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Home</Link>
              <Link to="/directory" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Directory</Link>
              <Link to="/events" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Events</Link>
              <Link to="/transportation" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Transportation</Link>
              <Link to="/emergency" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-teal-600 font-semibold' : 'text-teal-300'}`}>Emergency</Link>
              <Link to="/relocation" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>Relocation</Link>
            </div>
            <Link to="/deals" className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
              View Deals
            </Link>
          </div>
        </div>
      </nav>

      <EmergencyHero />
      <QuickDialBar />
      <EmergencyServicesMap />
      <ContactsGrid />
      <SafetyTipsSection />
      <AlertSourcesSection />
      <PrintableEmergencyCard />
      <EmergencyReportForm />
      <MailchimpNewsletter />

      {/* Data Sources Disclaimer */}
      <section className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500 text-lg"></i>
            </div>
            <div>
              <p className="text-xs text-gray-500 leading-relaxed">
                <strong className="text-gray-700">Data Sources:</strong> Emergency contact numbers and facility locations sourced from the Roseville Police Department, Roseville Fire Department, Placer County Office of Emergency Services, Sutter Roseville Medical Center, and Kaiser Permanente Roseville. Safety tips adapted from Ready.gov and Cal OES guidelines. All information is verified regularly but may change — please <strong>verify critical emergency information directly with the official agency</strong>.
              </p>
              <div className="flex items-center gap-4 mt-2">
                <p className="text-[11px] text-gray-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>Data last updated: March 1, 2025
                </p>
                <Link to="/data-sources" className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer">
                  View All Data Sources <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-800 via-teal-900 to-teal-800 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="text-2xl font-bold mb-4 block cursor-pointer">
                <span className="font-black">ROSEVILLE</span>
                <span className="text-amber-400">.org</span>
              </Link>
              <p className="text-white/80 text-sm leading-relaxed mb-4">Your official guide to Roseville, California. Discover attractions, events, dining, and everything our vibrant city has to offer.</p>
              <p className="text-white/80 text-sm">5098 Foothills Blvd, Suite 3-402<br />Roseville, California 95747</p>
            </div>
            <div className="emergency-numbers">
              <h4 className="text-lg font-bold mb-4"><a href="/emergency#contacts" className="hover:text-teal-300 transition-colors">Emergency Contacts</a></h4>
              <ul className="space-y-2">
                <li><span className="text-white/80 text-sm">Police/Fire/Medical: <strong>911</strong></span></li>
                <li><span className="text-white/80 text-sm">Police Non-Emergency: <strong>(916) 774-5000</strong></span></li>
                <li><span className="text-white/80 text-sm">Poison Control: <strong>(800) 222-1222</strong></span></li>
                <li><span className="text-white/80 text-sm">Crisis Lifeline: <strong>988</strong></span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4"><a href="/" className="hover:text-teal-300 transition-colors">Explore</a></h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/events" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Events</Link></li>
                <li><Link to="/directory" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Directory</Link></li>
                <li><Link to="/transportation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Transportation</Link></li>
                <li><Link to="/relocation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Relocation</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4"><a href="https://facebook.com" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-teal-300 transition-colors">Connect</a></h4>
              <div className="flex space-x-3 mb-6">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"><i className="ri-facebook-fill text-xl"></i></a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"><i className="ri-instagram-fill text-xl"></i></a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"><i className="ri-twitter-x-fill text-xl"></i></a>
              </div>
              <p className="text-white/80 text-sm"><i className="ri-phone-line mr-2"></i>(916) 238-8515</p>
              <p className="text-white/80 text-sm mt-2"><i className="ri-mail-line mr-2"></i>info@roseville.ca.us</p>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">&copy; 2025 City of Roseville, California. All rights reserved.</p>
              <div className="flex items-center space-x-6">
                <a href="/privacy" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="/terms" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Terms of Service</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Web Design</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}